import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string> {
	value: T;
	label: string;
	/** Optional mono-font prefix, e.g. '∨' / '∧' for combinators. */
	symbol?: string;
	icon?: LucideIcon;
}

interface SegmentedControlProps<T extends string> {
	value: T;
	onValueChange: (value: T) => void;
	options: SegmentedOption<T>[];
	size?: 'sm' | 'md';
}

/**
 * Radio-style multi-option picker rendered as a horizontal pill row. Built as
 * an accessible radiogroup of buttons (no shadcn RadioGroup dependency needed).
 */
export function SegmentedControl<T extends string>({ value, onValueChange, options, size = 'md' }: SegmentedControlProps<T>) {
	return (
		<div role="radiogroup" className="inline-flex items-center gap-1 rounded-md border border-border bg-muted p-0.5">
			{options.map(option => {
				const active = option.value === value;
				const Icon = option.icon;
				return (
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={active}
						onClick={() => onValueChange(option.value)}
						className={cn(
							'inline-flex items-center gap-1.5 rounded px-2.5 py-1 font-medium transition-colors',
							size === 'sm' ? 'text-xs' : 'text-sm',
							active ? 'bg-surface text-foreground shadow-elevation-1' : 'text-foreground-muted hover:text-foreground'
						)}>
						{option.symbol && <span className={cn('font-mono', active && 'text-brand-subtle-fg')}>{option.symbol}</span>}
						{Icon && <Icon size={size === 'sm' ? 12 : 14} />}
						{option.label}
					</button>
				);
			})}
		</div>
	);
}
